/**
 * CodeMirror-based feedback editor for the SPA.
 *
 * Provides syntax highlighting for feedback markup:
 * - Lyrics anchors: {@"lyrics text"}
 * - Recording references: [Recording @ 1:30]
 */

import { feedback } from "@bandwith/lezer-feedback";
import { themeAlpha, themeColors } from "@bandwith/styles";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { EditorState, type Extension } from "@codemirror/state";
import {
  placeholder as cmPlaceholder,
  EditorView,
  keymap,
} from "@codemirror/view";
import { tags as t } from "@lezer/highlight";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

/**
 * Syntax highlighting styles for feedback markup.
 */
const feedbackHighlighting = HighlightStyle.define([
  // Lyrics anchors - green like quoted strings
  {
    tag: t.special(t.string),
    color: themeColors.greenMuted,
    fontWeight: "500",
  },
  { tag: t.string, color: themeColors.greenMuted },
  { tag: t.brace, color: themeColors.textMuted },

  // Recording links - amber, like hyperlinks
  { tag: t.link, color: themeColors.amber },
  { tag: t.labelName, color: themeColors.amber, fontWeight: "500" },
  { tag: t.number, color: themeColors.copper },
  { tag: t.meta, color: themeColors.textMuted },
  { tag: t.squareBracket, color: themeColors.textMuted },
  { tag: t.operator, color: themeColors.textMuted },

  // Plain text - default text color
  { tag: t.content, color: themeColors.textSecondary },
]);

/**
 * Bandwith-themed CodeMirror theme for feedback editing
 */
const feedbackEditorTheme = EditorView.theme({
  "&": {
    backgroundColor: themeColors.bgPrimary,
    color: themeColors.textSecondary,
    fontSize: "14px",
  },
  ".cm-content": {
    padding: "12px",
    caretColor: themeColors.amber,
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: themeColors.amber,
    borderLeftWidth: "2px",
  },
  "&.cm-focused .cm-cursor": {
    borderLeftColor: themeColors.amber,
  },
  ".cm-activeLine": {
    backgroundColor: "transparent",
  },
  "&.cm-focused .cm-activeLine": {
    backgroundColor: themeAlpha("amber", 0.05),
  },
  ".cm-selectionBackground, ::selection": {
    backgroundColor: `${themeAlpha("amber", 0.2)} !important`,
  },
  "&.cm-focused .cm-selectionBackground": {
    backgroundColor: `${themeAlpha("amber", 0.25)} !important`,
  },
  ".cm-scroller": {
    overflow: "auto",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-placeholder": {
    color: themeColors.textDisabled,
    fontStyle: "italic",
  },
});

export type FeedbackCodeMirrorRef = {
  /** Insert text at the current cursor position */
  insertText: (text: string) => void;
  /** Focus the editor */
  focus: () => void;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  autoFocus?: boolean;
};

export const FeedbackCodeMirror = memo(
  forwardRef<FeedbackCodeMirrorRef, Props>(function FeedbackCodeMirror(
    {
      value,
      onChange,
      onSubmit,
      disabled = false,
      placeholder = "Write feedback...",
      className = "",
      hasError = false,
      autoFocus = false,
    },
    ref,
  ) {
    const editorRef = useRef<ReactCodeMirrorRef>(null);

    // Store onSubmit in a ref to avoid recreating extensions on every render
    const onSubmitRef = useRef(onSubmit);
    onSubmitRef.current = onSubmit;

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      insertText: (text: string) => {
        const view = editorRef.current?.view;
        if (!view) return;

        const { from, to } = view.state.selection.main;
        const transaction = view.state.update({
          changes: { from, to, insert: text },
          selection: { anchor: from + text.length },
        });
        view.dispatch(transaction);
        view.focus();
      },
      focus: () => {
        editorRef.current?.view?.focus();
      },
    }));

    // Build extensions
    const extensions = useMemo<Extension[]>(
      () => [
        feedback(),
        syntaxHighlighting(feedbackHighlighting),
        history(),
        keymap.of([
          // Mod+Enter to submit. This has to come before defaultKeymap, which
          // binds Mod-Enter to insertBlankLine — CodeMirror runs bindings in
          // array order and stops at the first that returns true, so listing
          // this second makes it dead code.
          {
            key: "Mod-Enter",
            run: () => {
              if (!onSubmitRef.current) return false;
              onSubmitRef.current();
              return true;
            },
          },
          ...defaultKeymap,
          ...historyKeymap,
        ]),
        cmPlaceholder(placeholder),
        EditorView.lineWrapping,
        EditorState.readOnly.of(disabled),
      ],
      [disabled, placeholder],
    );

    const handleChange = useCallback(
      (val: string) => {
        onChange(val);
      },
      [onChange],
    );

    const borderClass = hasError
      ? "border-red-muted focus-within:border-red-muted"
      : "border-border-subtle focus-within:border-amber";

    return (
      <CodeMirror
        ref={editorRef}
        value={value}
        onChange={handleChange}
        theme={feedbackEditorTheme}
        extensions={extensions}
        basicSetup={false}
        autoFocus={autoFocus}
        className={`rounded-lg border transition-colors ${borderClass} overflow-hidden ${className} ${
          disabled ? "opacity-50" : ""
        }`}
      />
    );
  }),
);
